import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Text,
  HStack,
  VStack,
  Link,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Switch,
  FormControl,
  FormLabel,
  Divider,
} from '@chakra-ui/react';
import { Cookie, Settings, BarChart2, Target } from 'lucide-react';
import { Link as ReactRouterLink } from 'react-router-dom';

const COOKIE_CONSENT_KEY = 'routeforecast_cookie_consent';
const CONSENT_VERSION = '1.0'; // Increment when policy changes

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    advertising: false,
  });

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { t } = useTranslation();

  useEffect(() => {
    // Check if user has already given consent
    const savedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);

    if (!savedConsent) {
      // Show banner after a short delay for better UX
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      try {
        const consent = JSON.parse(savedConsent);

        // Check if consent is for current version
        if (consent.version !== CONSENT_VERSION) {
          setShowBanner(true);
        } else {
          applyConsent(consent.preferences);
        }
      } catch (error) {
        setShowBanner(true);
      }
    }
  }, []);

  const applyConsent = prefs => {
    setPreferences(prefs);

    // Apply Google Analytics consent
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: prefs.analytics ? 'granted' : 'denied',
        ad_storage: prefs.advertising ? 'granted' : 'denied',
        ad_user_data: prefs.advertising ? 'granted' : 'denied',
        ad_personalization: prefs.advertising ? 'granted' : 'denied',
      });
    }

    // Store in localStorage for AdSlot component
    localStorage.setItem(
      'routeforecast_ads_enabled',
      prefs.advertising ? 'true' : 'false'
    );
  };

  const saveConsent = prefs => {
    const consent = {
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
      preferences: prefs,
    };

    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    applyConsent(prefs);
    setShowBanner(false);
    onClose();
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      advertising: true,
    };
    saveConsent(allAccepted);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      advertising: false,
    };
    saveConsent(onlyNecessary);
  };

  const handleCustomize = () => {
    onOpen();
  };

  const handleSaveCustom = () => {
    saveConsent(preferences);
  };

  const handleToggle = category => {
    if (category === 'necessary') return; // Can't disable necessary cookies

    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Banner */}
      <Box
        position="fixed"
        bottom="0"
        left="0"
        right="0"
        bg="white"
        boxShadow="0 -2px 10px rgba(0,0,0,0.1)"
        p={[4, 6]}
        zIndex="9999"
        borderTop="3px solid"
        borderColor="blue.500"
      >
        <Box maxW="1200px" mx="auto">
          <VStack spacing={4} align="stretch">
            <HStack spacing={2}>
              <Cookie size={18} color="#3182ce" />
              <Text fontSize={['sm', 'md']} fontWeight="medium">
                {t('cookies.title')}
              </Text>
            </HStack>
            <Text fontSize={['xs', 'sm']}>
              {t('cookies.description')}{' '}
              <Link
                as={ReactRouterLink}
                to="/cookies"
                color="blue.500"
                textDecoration="underline"
              >
                {t('cookies.moreInfo')}
              </Link>
            </Text>

            <HStack spacing={2} flexWrap="wrap">
              <Button
                colorScheme="blue"
                size={['sm', 'md']}
                onClick={handleAcceptAll}
                flex={['1 1 100%', '0 1 auto']}
              >
                {t('cookies.acceptAll')}
              </Button>
              <Button
                variant="outline"
                size={['sm', 'md']}
                onClick={handleRejectAll}
                flex={['1 1 100%', '0 1 auto']}
              >
                {t('cookies.rejectOptional')}
              </Button>
              <Button
                variant="ghost"
                size={['sm', 'md']}
                onClick={handleCustomize}
                flex={['1 1 100%', '0 1 auto']}
              >
                {t('cookies.customize')}
              </Button>
            </HStack>
          </VStack>
        </Box>
      </Box>

      {/* Customization Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent mx={4}>
          <ModalHeader>{t('cookies.modalTitle')}</ModalHeader>
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.600">
                {t('cookies.modalBody')}
              </Text>

              <Divider />

              {/* Necessary Cookies */}
              <FormControl
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box flex="1" mr={4}>
                  <FormLabel mb="1" fontWeight="bold" fontSize="sm">
                    <HStack spacing={1} as="span" display="inline-flex">
                      <Settings size={14} />
                      <span>{t('cookies.necessaryTitle')}</span>
                    </HStack>
                  </FormLabel>
                  <Text fontSize="xs" color="gray.600">
                    {t('cookies.necessaryDesc')}
                  </Text>
                </Box>
                <Switch
                  isChecked={preferences.necessary}
                  isDisabled
                  colorScheme="blue"
                />
              </FormControl>

              <Divider />

              {/* Analytics Cookies */}
              <FormControl
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box flex="1" mr={4}>
                  <FormLabel mb="1" fontWeight="bold" fontSize="sm">
                    <HStack spacing={1} as="span" display="inline-flex">
                      <BarChart2 size={14} />
                      <span>{t('cookies.analyticsTitle')}</span>
                    </HStack>
                  </FormLabel>
                  <Text fontSize="xs" color="gray.600">
                    {t('cookies.analyticsDesc')}
                  </Text>
                </Box>
                <Switch
                  isChecked={preferences.analytics}
                  onChange={() => handleToggle('analytics')}
                  colorScheme="blue"
                />
              </FormControl>

              <Divider />

              {/* Advertising Cookies */}
              <FormControl
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box flex="1" mr={4}>
                  <FormLabel mb="1" fontWeight="bold" fontSize="sm">
                    <HStack spacing={1} as="span" display="inline-flex">
                      <Target size={14} />
                      <span>{t('cookies.advertisingTitle')}</span>
                    </HStack>
                  </FormLabel>
                  <Text fontSize="xs" color="gray.600">
                    {t('cookies.advertisingDesc')}
                  </Text>
                </Box>
                <Switch
                  isChecked={preferences.advertising}
                  onChange={() => handleToggle('advertising')}
                  colorScheme="blue"
                />
              </FormControl>

              <Divider />

              <Text fontSize="xs" color="gray.500">
                {t('cookies.moreInfoNote')}{' '}
                <Link
                  as={ReactRouterLink}
                  to="/cookies"
                  color="blue.500"
                  textDecoration="underline"
                >
                  {t('cookies.cookiesLink')}
                </Link>{' '}
                {t('cookies.and')}{' '}
                <Link
                  as={ReactRouterLink}
                  to="/privacidad"
                  color="blue.500"
                  textDecoration="underline"
                >
                  {t('cookies.privacyLink')}
                </Link>
                .
              </Text>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              {t('cookies.cancelButton')}
            </Button>
            <Button colorScheme="blue" onClick={handleSaveCustom}>
              {t('cookies.saveButton')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

// Export function to open preferences from anywhere
export const openCookiePreferences = () => {
  window.dispatchEvent(new CustomEvent('open-cookie-preferences'));
};

// Export function to check if consent has been given
export const hasConsentFor = category => {
  try {
    const savedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!savedConsent) return false;

    const consent = JSON.parse(savedConsent);
    return consent.preferences[category] || false;
  } catch {
    return false;
  }
};
